/**
 * SessionTimer Component
 *
 * A reusable component that displays a live countdown timer for broker session expiry.
 * Extracted from BrokerAccountDetails to be used across different views.
 */

import React, { useState, useEffect } from "react";
import { Typography, LinearProgress, Box } from "@mui/material";
import {
  Timer as TimerIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Icon } from "../../../components/ds";

const SessionTimer = ({
  sessionExpiresAt,
  variant = "full", // "full", "compact", "chip-only"
  showProgress = true,
  showIcon = true,
  size = "small", // "small", "medium", "large"
  label, // chip-only: optional leading text, e.g. "Session active" → "Session active · 3h 18m"
}) => {
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  const [progress, setProgress] = useState(100);

  // Calculate and update session time remaining
  useEffect(() => {
    if (!sessionExpiresAt) return;

    const calculateTimeLeft = () => {
      const expiryTime = new Date(sessionExpiresAt).getTime();
      const now = new Date().getTime();
      const timeLeft = expiryTime - now;

      if (timeLeft <= 0) {
        setSessionTimeLeft(null);
        setProgress(0);
        return;
      }

      // Calculate total session duration (assuming 24 hours)
      const totalDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const progressValue = Math.max(
        0,
        Math.min(100, (timeLeft / totalDuration) * 100)
      );

      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setSessionTimeLeft({ hours, minutes, seconds });
      setProgress(progressValue);
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft(); // Initial calculation

    return () => clearInterval(timer);
  }, [sessionExpiresAt]);

  // Function to get the theme color based on remaining time
  const getTimeBasedColor = (theme, hours) => {
    // Expired: sessionTimeLeft is null, so `hours` arrives as undefined. Both
    // comparisons below are false for undefined, so this used to FALL THROUGH to
    // green — the timer stayed green at the exact moment the session died, which
    // is "not changing colour when the time is going to end". Expiry is the most
    // alarming state; it must read red.
    if (hours == null) return theme.palette.error.main;
    if (hours < 1) return theme.palette.error.main;
    if (hours < 2) return theme.palette.warning.main;
    return theme.palette.mode === "dark" ? "#81c784" : "#2e7d32";
  };

  // Function to get status for chip display
  const getSessionStatus = () => {
    if (!sessionTimeLeft)
      return { label: "Expired", color: "error", severity: "error" };
    if (sessionTimeLeft.hours < 1)
      return { label: "Critical", color: "error", severity: "error" };
    if (sessionTimeLeft.hours < 2)
      return { label: "Warning", color: "warning", severity: "warning" };
    return { label: "Active", color: "success", severity: "success" };
  };

  // Function to format time display
  const formatTimeLeft = (timeLeft, compact = false) => {
    if (!timeLeft) return "Expired";
    if (compact) {
      if (timeLeft.hours < 1) {
        return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
      }
      return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    }
    if (timeLeft.hours < 1) {
      return `${timeLeft.minutes}m ${timeLeft.seconds}s remaining`;
    }
    return `${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s remaining`;
  };

  // Get icon based on status
  const getStatusIcon = (iconSize) => {
    const sx = iconSize ? { fontSize: iconSize } : undefined;
    if (!sessionTimeLeft) return <Icon name="error" size={iconSize} />;
    if (sessionTimeLeft.hours < 1) return <Icon name="error" size={iconSize} />;
    if (sessionTimeLeft.hours < 2) return <Icon name="pending" size={iconSize} />;
    return <TimerIcon sx={sx} />;
  };

  const status = getSessionStatus();

  // Chip-only variant for compact display
  if (variant === "chip-only") {
    const badgeVariant =
      status.color === "error"
        ? "loss"
        : status.color === "warning"
        ? "warn"
        : "profit";
    const badgeSize = size === "large" ? "lg" : size === "medium" ? "" : "sm";
    const time = sessionTimeLeft ? formatTimeLeft(sessionTimeLeft, true) : null;
    // badgeVariant already goes red on expiry (getSessionStatus → "error"), unlike
    // the getTimeBasedColor path the other variants use — this is the correct one.
    return (
      <span className={`ds-badge ${badgeSize} ${badgeVariant}`.trim()}>
        {showIcon && getStatusIcon(13)}
        {label ? `${label}${time ? ` · ${time}` : ""}` : time || "Expired"}
      </span>
    );
  }

  // Progress-only variant: a thin full-width live bar, no text. On phone the time
  // already reads inside the session pill, so this is just the "session is alive and
  // draining" indicator — the value the desktop lane carries — without reintroducing
  // a boxed lane. Colour tracks time-left (green → amber → red, red at expiry) via the
  // same getTimeBasedColor used everywhere else.
  if (variant === "progress") {
    return (
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          borderRadius: 999,
          backgroundColor: (theme) =>
            alpha(
              getTimeBasedColor(theme, sessionTimeLeft?.hours),
              theme.palette.mode === "dark" ? 0.18 : 0.12
            ),
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            transition: "transform 0.6s ease, background-color 0.3s",
            backgroundColor: (theme) =>
              getTimeBasedColor(theme, sessionTimeLeft?.hours),
          },
        }}
      />
    );
  }

  // Compact variant for list views
  if (variant === "compact") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {showIcon && (
          <Box
            sx={{
              color: (theme) =>
                getTimeBasedColor(theme, sessionTimeLeft?.hours),
              display: "flex",
              alignItems: "center",
            }}
          >
            {getStatusIcon()}
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            color: (theme) => getTimeBasedColor(theme, sessionTimeLeft?.hours),
            fontWeight: sessionTimeLeft?.hours < 2 ? 600 : 400,
          }}
        >
          {formatTimeLeft(sessionTimeLeft, true)}
        </Typography>
        {showProgress && sessionTimeLeft && (
          <Box sx={{ width: 40, ml: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 3,
                borderRadius: 3,
                backgroundColor: (theme) =>
                  sessionTimeLeft?.hours < 1
                    ? alpha(theme.palette.error.main, 0.2)
                    : sessionTimeLeft?.hours < 2
                    ? alpha(theme.palette.warning.main, 0.2)
                    : alpha(theme.palette.success.main, 0.2),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor: (theme) =>
                    getTimeBasedColor(theme, sessionTimeLeft?.hours),
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  // Full variant for detailed display
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {showIcon && (
          <Box
            sx={{
              color: (theme) =>
                getTimeBasedColor(theme, sessionTimeLeft?.hours),
              display: "flex",
              alignItems: "center",
            }}
          >
            {getStatusIcon()}
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            color: (theme) => getTimeBasedColor(theme, sessionTimeLeft?.hours),
            fontWeight: sessionTimeLeft?.hours < 2 ? 600 : 400,
            display: "flex",
            alignItems: "center",
            "& strong": {
              ml: 0.5,
              fontWeight: 600,
            },
          }}
        >
          {sessionTimeLeft ? (
            <>
              Session expires in:{" "}
              <strong>
                {sessionTimeLeft.hours < 1 ? (
                  <span style={{ color: "error.main" }}>
                    {`${sessionTimeLeft.minutes}m ${sessionTimeLeft.seconds}s`}
                  </span>
                ) : (
                  `${sessionTimeLeft.hours}h ${sessionTimeLeft.minutes}m ${sessionTimeLeft.seconds}s`
                )}
              </strong>
            </>
          ) : (
            <span style={{ color: "error.main" }}>Session Expired</span>
          )}
        </Typography>
      </Box>
      {showProgress && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: (theme) =>
              sessionTimeLeft?.hours < 1
                ? alpha(
                    theme.palette.error.main,
                    theme.palette.mode === "dark" ? 0.2 : 0.1
                  )
                : sessionTimeLeft?.hours < 2
                ? alpha(
                    theme.palette.warning.main,
                    theme.palette.mode === "dark" ? 0.2 : 0.1
                  )
                : alpha(
                    theme.palette.success.main,
                    theme.palette.mode === "dark" ? 0.2 : 0.1
                  ),
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              transition: "all 0.3s",
              backgroundColor: (theme) =>
                sessionTimeLeft?.hours < 1
                  ? theme.palette.error.main
                  : sessionTimeLeft?.hours < 2
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
            },
          }}
        />
      )}
    </Box>
  );
};

export default SessionTimer;
